import {
    getRatePlan,
    listRatePlans
} from './queries';
import {
    createRatePlan,
    deleteRatePlan,
    updateRatePlan,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {ChargeTier, RatePlan} from "../types.ts";
import { billingApiClient } from "../../billing-api-integration";
import {ListRecordsQuery} from "../../../types";


export type ListRatePlans = {
    listRatePlans: { items: RatePlan[], nextToken: string }
}

export type GetRatePlan = {
    getRatePlan: RatePlan
}

export type CreateRatePlan = {
    createRatePlan: RatePlan
}

export type UpdateRatePlan = {
    updateRatePlan: RatePlan
}

export type DeleteRatePlan = {
    deleteRatePlan: string
}


const client = billingApiClient;

export const fetchRecords = async (query: ListRecordsQuery) => {
    const response = await client.graphql<GraphQLQuery<ListRatePlans>>({
                                                                           query: listRatePlans,
                                                                           variables: {
                                                                               ...query
                                                                           },
                                                                       });
    return {
        items: response.data?.listRatePlans.items,
        nextToken: response.data?.listRatePlans.nextToken,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetRatePlan>>({
                                                                         query: getRatePlan,
                                                                         variables: {id: id},
                                                                     });
    return response.data?.getRatePlan;
};

// ChargeTier.rate is `Int!` on the API — MINOR UNITS (cents). A Mantine number input can
// hand back a string ("50"), and AppSync then rejects the whole mutation with the opaque
// "Variable 'rate' has an invalid value". Coerce at the boundary so a numeric-looking
// string still lands as a number.
//
// A non-integer (0.5) is NOT silently rounded — rounding a money field behind the user's
// back is worse than refusing it. The form blocks decimals; this is the backstop.
// FNP-836: coerce a band to a whole number, refusing anything that is not one. Same
// discipline as `rate` — a fractional or non-numeric band is refused, never rounded,
// because a silently-adjusted band changes which tier a quantity falls into.
const toIntBand = (value: unknown, field: string): number => {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isInteger(n)) {
        throw new Error(
            `Charge tier ${field} must be a whole number (got "${value}").`
        );
    }
    return n;
};

const toChargeTierInput = (tiers?: ChargeTier[]) =>
    tiers?.map((tier) => {
        const rate = typeof tier.rate === 'number' ? tier.rate : Number(tier.rate);
        if (!Number.isInteger(rate)) {
            throw new Error(
                `Charge tier rate must be a whole number of cents (got "${tier.rate}"). ` +
                `Enter 50 for $0.50.`
            );
        }
        // FNP-836: the bands are `Int!` / `Int` on the API. Mantine's number input can hand
        // back a string ("100"), and the stored legacy rows are strings too, so coerce here
        // the same way `rate` is — otherwise AppSync rejects with the opaque
        // "Variable 'lowerBand' has an invalid value".
        const lowerBand = toIntBand(tier.lowerBand, 'lowerBand');
        // Blank/null upperBand means UNBOUNDED (top tier only) — preserve the null rather
        // than coercing it to 0, which would silently make the tier hold nothing.
        const upperBand = tier.upperBand === null || tier.upperBand === undefined || (tier.upperBand as unknown) === ''
            ? null
            : toIntBand(tier.upperBand, 'upperBand');

        return {
            lowerBand,
            upperBand,
            rate,
        };
    });

// FNP-1739: name exactly the CreateRatePlanInput fields. The Add drawer seeds `id: ''`, and
// AppSync refuses the whole mutation for any key the input type does not declare.
const toCreateRatePlanInput = (payload: RatePlan) => ({
    name: payload.name,
    billableUnitId: payload.billableUnitId,
    billablePeriod: payload.billablePeriod,
    chargeTiers: toChargeTierInput(payload.chargeTiers),
    status: payload.status,
    effectiveFrom: payload.effectiveFrom,
    effectiveTo: payload.effectiveTo,
});

export const add = async (payload: RatePlan) => {
    const response = await client.graphql<GraphQLQuery<CreateRatePlan>>(
        {
            query: createRatePlan,
            variables: {input: toCreateRatePlanInput(payload)},
        });
    return response.data?.createRatePlan;
};

// `_id` is unused: UpdateRatePlanInput carries the id inside the record itself. Kept in
// the signature to match the shape every other service in this app exposes.
export const update = async (_id: string, record: RatePlan) => {

    delete record.created;
    delete record.updated;

    const response = await client.graphql<GraphQLQuery<UpdateRatePlan>>({
        query: (updateRatePlan),
        variables: {input: {...record, chargeTiers: toChargeTierInput(record.chargeTiers)}},
    });
    return response.data?.updateRatePlan;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteRatePlan>>({
                                                                            query: (deleteRatePlan),
                                                                            variables: {input: {id: id}},

                                                                        });
    return response.data?.deleteRatePlan;
};
